import type { Vec3 } from '../../geo/types';
import { transformPoint, type Frame } from './frameMath';
import type { MeshBuilder, VertexStyle } from './meshBuilder';

/* Two faces reach a shared corner along different sums, which differ in their last bits. The
   ground is read at the nearest tenth of a millimetre, so the corner gets one height and the
   faces still meet. */
const WELD_M = 1e-4;

function refuse(solid: string): never {
  throw new Error(`A ${solid} cannot follow the ground: only faces are stood on it point by point`);
}

/**
 * Stands local geometry on ground that is not level: every vertex of a face is raised by the
 * ground under it. A city wall runs for a kilometre and more, and one lift for the whole of it
 * (elevatedBuilder.ts) hangs it in the air wherever the ground is lower than at the point the lift
 * was read. A face over ground at zero is passed on as it is.
 *
 * `groundAt` is the height of the ground under a world point. The painter's exaggeration counts
 * heights from the ground under each face, as it does from a lifted building's.
 */
export function drapedBuilder(
  builder: MeshBuilder,
  groundAt: (world: Vec3) => number
): MeshBuilder {
  const raised = <T extends readonly Vec3[]>(
    style: VertexStyle,
    frame: Frame,
    points: T
  ): { readonly style: VertexStyle; readonly points: T } | null => {
    const lifts = points.map((p) => {
      const [x, y, z] = transformPoint(p, frame);
      return groundAt([Math.round(x / WELD_M) * WELD_M, y, Math.round(z / WELD_M) * WELD_M]);
    });
    if (lifts.every((lift) => lift === 0)) return null;
    const under = lifts.reduce((sum, lift) => sum + lift, 0) / lifts.length;
    return {
      style: style.exaggeration
        ? {
            ...style,
            exaggeration: { ...style.exaggeration, base: style.exaggeration.base + under },
          }
        : style,
      points: points.map((p, i): Vec3 => [p[0], p[1] + lifts[i]!, p[2]]) as unknown as T,
    };
  };
  return {
    quad: (style, frame, corners, uv) => {
      const stood = raised(style, frame, corners);
      if (stood) builder.quad(stood.style, frame, stood.points, uv);
      else builder.quad(style, frame, corners, uv);
    },
    polygon: (style, frame, outline, uvOf) => {
      const stood = raised(style, frame, outline);
      if (!stood) {
        builder.polygon(style, frame, outline, uvOf);
        return;
      }
      // A face's pattern is laid out from its own points, so it does not stretch with the ground.
      const own = new Map(stood.points.map((p, i) => [p, outline[i]!]));
      builder.polygon(stood.style, frame, stood.points, uvOf && ((p) => uvOf(own.get(p) ?? p)));
    },
    box: () => refuse('box'),
    prism: () => refuse('prism'),
    cylinder: () => refuse('cylinder'),
    groundPolygon: () => refuse('ground polygon'),
    get vertexCount() {
      return builder.vertexCount;
    },
    get triangleCount() {
      return builder.triangleCount;
    },
    finish: () => builder.finish(),
    build: () => builder.build(),
  };
}
